import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  HStack,
  Button,
  ModalFooter,
  useToast,
  Flex,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import slugify from 'slugify';
import context from '../context';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import Checkbox from '../../Form/Checkbox';
import {
  CREATE_PAGE_GROUP,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Textarea from '../../Form/Textarea';
import sanitizeString from '../../../utils/sanitizeString';
import Select from '../../Form/Select';
import filtersType from '../../../utils/filtersType';
import auth from '../../../apis/auth';
import AutocompleteSelect from '../../Form/AutocompleteSelect';

interface Props {
  onCreated?: (data: any) => void;
}

export type CategoryType = {
  Quantity: number;
  Position: number;
  Name: string;
  Link: string;
  LinkEncoded: string;
  Map: string;
  Value: string;
};

export type CategoriesOptionsType = {
  label: string;
  value: string;
};

interface FormData {
  name: string;
  type: {
    label: string | string;
    value: string | string;
  };
  category_id: {
    label: string | string;
    value: string | string;
  };
  description?: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  type: yup
    .object()
    .shape({
      value: yup.string().required('Tipo é obrigatório'),
      label: yup.string().required('Tipo é obrigatório'),
    })
    .required('Tipo é obrigatório'),
  category_id: yup
    .object()
    .shape({
      value: yup.string().required('Categoria do Filtro é obrigatório'),
      label: yup.string().required('Categoria do Filtro é obrigatório'),
    })
    .required('Categoria do Filtro é obrigatório'),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('Data de início é obrigatória'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(schedule_start, 'Data final deve ser superior a data inicial')
          .required('Data Final é obrigatória');
      }
      return yup.date().required('Data Final é obrigatória');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const {
    pageGroup,
    newPageGroupDTO,
    onPageGroupCreated,
    onClose,
    display,
    page,
  } = React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver: yupResolver(formData),
  });

  const [createPageGroup, { loading: createLoading, error: createError }] =
    useMutation(CREATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [updatePageGroup, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [removePageGroup, { loading: deleteLoading, error: deleteError }] =
    useMutation(REMOVE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);
  const [categoriesOptions, setCategoriesOptions] = useState<
    CategoriesOptionsType[]
  >([]);

  const { errors } = formState;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function getCategories() {
    if (!page?.vtex_query) {
      const confirmed = window.confirm(
        'Só é possivel criar filtros em vitrines dinâmicas',
      );
      if (confirmed) {
        onClose();
      }
    } else {
      const { data: apiData } = await auth.get<any>(
        `bridge/vtex/api/catalog_system/pub/facets/search/${page?.vtex_query}`,
      );

      const tempOptions = [];

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const key in apiData?.SpecificationFilters) {
        tempOptions.push({
          label: key,
          value: slugify(key, { lower: true }),
        });
      }

      setCategoriesOptions(tempOptions);
    }
  }
  useEffect(() => {
    getCategories();
    if (createError || updateError || deleteError) {
      toast({
        title: 'Opss!!',
        description:
          createError?.message ||
          updateError?.message ||
          deleteError?.message ||
          'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, updateError, toast, deleteError]);

  if (pageGroup) {
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
      setValue('countdown', pageGroup.countdown);
    }
    setValue('description', pageGroup?.description);
    setValue('name', pageGroup.name);
    setValue(
      'type',
      filtersType.find(type => type?.value === pageGroup?.type),
    );

    setValue(
      'category_id',
      categoriesOptions.find(
        (category: CategoriesOptionsType) =>
          category?.label === pageGroup?.category_id,
      ),
    );
  }
  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, ...rest } = sanitizedObj;
      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              id: pageGroup.id,
              schedule_end: scheduleData ? rest.schedule_end : null,
              countdown: scheduleData ? rest.countdown : false,
              type: data.type?.value,
              category_id: data.category_id?.label,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        toast({
          title: 'Sucesso!',
          description: 'Grupo editado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              element_display_id: display?.id,
              type: data.type?.value,
              category_id: data.category_id?.label,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: 'Sucesso!',
          description: 'Grupo criado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'Tem certeza que deseja excluir o grupo?',
      );
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: 'Sucess!!',
          description: 'Grupo deletado com sucesso',
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <>
      <VStack as="form" spacing="8" onSubmit={handleSubmit(handleForm)}>
        <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
          <Input
            placeholder="Título"
            error={errors?.name}
            {...register('name')}
          />
        </SimpleGrid>
        <Textarea
          placeholder="Subtítulo"
          error={errors?.description}
          {...register('description')}
        />
        <Select
          name="type"
          width="150px"
          control={control}
          placeholder="Tipo"
          error={errors.type}
          options={filtersType}
          onChange={() => {
            setValue('type', '');
          }}
        />
        <AutocompleteSelect
          label="Categoria do Filtro"
          control={control}
          error={errors.category_id}
          options={categoriesOptions}
          name="category_id"
        />
        <Flex width="100%" justifyContent="left" />
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            Agendamento
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label="Início"
                showTimeSelect
                error={errors?.schedule_start}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label="Final"
                showTimeSelect
                error={errors?.schedule_end}
              />
            </SimpleGrid>
            <Checkbox
              colorScheme="brand"
              control={control}
              label="Contagem Regressiva"
              {...register('countdown')}
            />
          </>
        )}
      </VStack>
      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Fechar</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading}
            onClick={handleSubmit(handleForm)}
          >
            Salvar
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default Form;
